import {
  ClockInput,
  AmountField,
  clearIncident,
  DatePick,
  Dialog,
  formatMoney,
  incidentKinds,
  InvalidInput,
  refunds,
  setIncident,
  updateStop,
  type Booking,
  type IncidentInput,
  type IncidentKind,
  type Plan,
  type PlanStop,
  type Refund,
} from '@trippath/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { arrivalAt, instantOf, later, timeOf, wallOf } from '../../lib/clock';
import { dayRange } from '../../lib/countryFacts';
import { day as dayOf, problemKey } from '../../lib/format';
import { arrivesAt, leavesAt } from '../../lib/gaps';
import { withWayAt } from '../../lib/stopInput';

/** What comes of a cancelled booking: another bought in its place, or what of its money. */
type Outcome = 'replaced' | Refund;

/** The outcomes of a cancelled booking, the replacement first: only a ticket has one. */
const OUTCOMES = ['replaced', ...refunds] as const;

/**
 * Whether this booking was replaced: another bought in its place, its money going on that one.
 * One whose money is gone, or coming back, says so even when another was bought after it.
 */
export function replacedIn(plan: Plan, booking: Booking): boolean {
  return (
    booking.incident?.refund === 'received' &&
    plan.stops.some((stop) => stop.bookings.some((b) => b.replaces === booking.id))
  );
}

/** Where a way leaves and arrives: a late one's times are each local there. */
export interface Zones {
  from: string | null;
  to: string | null;
}

const NO_ZONES: Zones = { from: null, to: null };

/** Where a stop's way on leaves and arrives: its own zone, and the next stop's. */
export function zonesOf(plan: Plan, stop: PlanStop): Zones {
  return { from: stop.zone, to: plan.stops[plan.stops.indexOf(stop) + 1]?.zone ?? null };
}

/** What happened to a booking in a few words, and whether it is cancelled; none when nothing. */
export function useIncidentMark() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (booking: Booking, replaced: boolean, zones: Zones = NO_ZONES) => {
    const { incident } = booking;
    if (incident === null) return null;
    const money = formatMoney(incident.amount ?? booking.price, lang);
    const clock = (at: string | null, zone: string | null) =>
      at === null ? '…' : wallOf(at, zone).time;
    const text =
      incident.kind === 'moved'
        ? t('incident.chipMoved')
        : incident.kind === 'late'
          ? t('incident.chipLate', {
              times: `${clock(incident.leavesAt, zones.from)} → ${clock(incident.arrivesAt, zones.to)}`,
            })
          : replaced
            ? t('incident.chipReplaced')
            : incident.refund === 'pending'
              ? t('incident.chipPending', { amount: money })
              : incident.refund === 'received'
                ? t('incident.chipReceived')
                : incident.refund === 'voucher'
                  ? incident.voucherUntil === null
                    ? t('incident.chipVoucher', { amount: money })
                    : t('incident.chipVoucherUntil', {
                        amount: money,
                        until: dayOf(incident.voucherUntil, lang),
                      })
                  : incident.lost.minor > 0
                    ? t('incident.chipLost', { amount: formatMoney(incident.lost, lang) })
                    : t('incident.chipCancelled');
    return { text, cancelled: incident.kind === 'cancelled' };
  };
}

/** A booking's mark of what happened to it: red when cancelled, amber when its time is not. */
export function IncidentMark({
  booking,
  replaced,
  zones = NO_ZONES,
}: {
  booking: Booking;
  replaced: boolean;
  zones?: Zones;
}) {
  const mark = useIncidentMark()(booking, replaced, zones);
  if (mark === null) return null;
  // A space before it, said only with it.
  return (
    <>
      {' '}
      <span className={mark.cancelled ? 'incident-mark incident-cancelled' : 'incident-mark'}>
        {mark.text}
      </span>
    </>
  );
}

interface Props {
  plan: Plan;
  /** The stop the booking is kept on: a ticket's way on is this stop's. */
  stop: PlanStop;
  booking: Booking;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

/**
 * Something happened to a booking (the Branches mockup, §4): a ticket's time changed, it is late,
 * or it was cancelled — a flight, a bus, a train, a ferry or a car alike; a room can only be
 * cancelled. A cancelled one says what came of it: another in its place, money coming back or
 * back, a voucher, or the money gone, a loss in the budget.
 */
export function IncidentDialog({ plan, stop, booking, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const was = booking.incident;
  const { from, to } = zonesOf(plan, stop);
  // Only a ticket for the way on has a time to change.
  const timed = booking.kind === 'ticket' && stop.out !== null;
  const kinds = timed ? incidentKinds : (['cancelled'] as const);
  const outcomes = booking.kind === 'ticket' ? OUTCOMES : refunds;
  const [kind, setKind] = useState<IncidentKind>(was?.kind ?? 'cancelled');
  const [outcome, setOutcome] = useState<Outcome>(
    replacedIn(plan, booking) ? 'replaced' : (was?.refund ?? outcomes[0]),
  );
  // Its times as said when late, else as planned.
  const leaving = (was?.kind === 'late' ? was.leavesAt : null) ?? leavesAt(stop);
  const arriving = (was?.kind === 'late' ? was.arrivesAt : null) ?? arrivesAt(stop);
  const [leavesDay, setLeavesDay] = useState(
    leaving === null ? stop.end : wallOf(leaving, from).day,
  );
  const [leaves, setLeaves] = useState(leaving === null ? '' : wallOf(leaving, from).time);
  const [arrives, setArrives] = useState(arriving === null ? '' : wallOf(arriving, to).time);
  const [amount, setAmount] = useState<number | null>(
    was?.amount?.minor ?? booking.price.minor,
  );
  const [until, setUntil] = useState(was?.voucherUntil ?? null);
  const [failed, setFailed] = useState<string | null>(null);
  const [wrong, setWrong] = useState(false);
  // When it was planned: a ticket's way when it leaves, a room its nights.
  const goes = timed ? leavesAt(stop) : null;
  const planned =
    goes !== null
      ? `${dayOf(wallOf(goes, from).day, lang)} ${wallOf(goes, from).time}`
      : booking.kind === 'stay' && booking.day !== null
        ? dayRange(booking.day, later(booking.day, booking.nights), lang)
        : null;

  const done = (saving: Promise<Plan>) => {
    setFailed(null);
    saving.then(onSaved, (error: unknown) => {
      const [first] = error instanceof InvalidInput ? error.fields : [];
      setFailed(t(first ? problemKey(first.problem) : 'plan.failed'));
    });
  };

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (kind === 'cancelled') {
      // A replacement: its money goes on the one bought in its place.
      const refund = outcome === 'replaced' ? 'received' : outcome;
      const input: IncidentInput = {
        kind,
        refund,
        amount: refund === 'lost' || outcome === 'replaced' ? null : amount,
        voucherUntil: refund === 'voucher' ? until : null,
      };
      done(setIncident(plan.trip.id, booking.id, input));
      return;
    }
    const left = timeOf(leaves);
    const came = timeOf(arrives);
    if (typeof left !== 'string' || came === undefined) {
      setWrong(true);
      return;
    }
    const departureAt = instantOf(leavesDay, left, from);
    const arrivalAtNew = came === null ? null : arrivalAt(departureAt, came, null, from, to);
    done(
      kind === 'moved'
        ? // A new time is the way's own: the stops after it follow.
          updateStop(plan.trip.id, stop.id, withWayAt(stop, departureAt, arrivalAtNew)).then(
            () => setIncident(plan.trip.id, booking.id, { kind }),
          )
        : setIncident(plan.trip.id, booking.id, {
            kind,
            leavesAt: departureAt,
            arrivesAt: arrivalAtNew,
          }),
    );
  };

  const time = (id: string, label: string, value: string, set: (v: string) => void) => (
    <div className="field">
      <label className="field-label" htmlFor={`${ids}-${id}`}>
        {label}
      </label>
      <ClockInput
        id={`${ids}-${id}`}
        className="text-input booking-time"
        value={value}
        aria-invalid={wrong}
        onChange={(next) => {
          set(next);
          setWrong(false);
        }}
      />
    </div>
  );

  return (
    <Dialog
      open
      title={t(timed ? 'incident.title' : 'incident.titleStay', { name: booking.name })}
      onClose={onClose}
    >
      <form className="form incident-form" onSubmit={save} noValidate>
        <p className="quiet">
          {planned === null
            ? formatMoney(booking.price, lang)
            : `${t('incident.planned', { when: planned })} · ${formatMoney(booking.price, lang)}`}
        </p>
        {timed && (
          <div className="field">
            <span className="field-label" id={`${ids}-kind`}>
              {t('incident.what')}
            </span>
            <div className="segmented" role="group" aria-labelledby={`${ids}-kind`}>
              {kinds.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={kind === option}
                  onClick={() => {
                    setKind(option);
                  }}
                >
                  {t(`incident.${option}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        {kind === 'cancelled' ? (
          <fieldset className="incident-outcomes">
            <legend className="field-label">{t('incident.outcome')}</legend>
            {outcomes.map((option) => (
              <label
                key={option}
                className={
                  outcome === option ? 'incident-choice incident-choice-on' : 'incident-choice'
                }
              >
                <input
                  type="radio"
                  name={`${ids}-outcome`}
                  checked={outcome === option}
                  onChange={() => {
                    setOutcome(option);
                  }}
                />
                <strong>{t(`incident.${option}`)}</strong>
                <small>{t(`incident.${option}Hint`)}</small>
              </label>
            ))}
            {(outcome === 'pending' || outcome === 'received' || outcome === 'voucher') && (
              <div className="form-row">
                <AmountField
                  label={t(outcome === 'voucher' ? 'incident.worth' : 'incident.amount')}
                  value={amount}
                  onChange={setAmount}
                  currency={booking.price.currency}
                />
                {outcome === 'voucher' && (
                  <DatePick label={t('incident.until')} value={until} onChange={setUntil} />
                )}
              </div>
            )}
          </fieldset>
        ) : (
          <>
            <p className="quiet">{t(`incident.${kind}Hint`)}</p>
            <div className="form-row">
              <DatePick
                label={t('incident.leavesDay')}
                required
                value={leavesDay}
                onChange={(next) => {
                  if (next !== null) setLeavesDay(next);
                }}
              />
              <div className="booking-times">
                {time('leaves', t('incident.leaves'), leaves, setLeaves)}
                {time('arrives', t('incident.arrives'), arrives, setArrives)}
              </div>
            </div>
            {wrong && <p className="field-error">{t('incident.timeWrong')}</p>}
          </>
        )}
        {failed !== null && (
          <p className="field-error" role="alert">
            {failed}
          </p>
        )}
        <div className="dialog-actions">
          {was !== null && (
            <button
              type="button"
              className="button button-quiet dialog-action-apart"
              onClick={() => {
                done(clearIncident(plan.trip.id, booking.id));
              }}
            >
              {t('incident.clear')}
            </button>
          )}
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('incident.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
